<template>
  <div class="apply-review-page">
    <!-- 页面头部 -->
    <page-header>
      <div>进件申请审核</div>
    </page-header>

    <!-- 统计卡片 -->
    <div class="stats-cards">
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="stat-card pending">
            <div class="stat-icon"><i class="el-icon-time"></i></div>
            <div class="stat-content">
              <div class="stat-value">{{ stats.status2 || 0 }}</div>
              <div class="stat-label">待代理初审</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card reviewing">
            <div class="stat-icon"><i class="el-icon-loading"></i></div>
            <div class="stat-content">
              <div class="stat-value">{{ stats.status3 || 0 }}</div>
              <div class="stat-label">微信审核中</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card approved">
            <div class="stat-icon"><i class="el-icon-check"></i></div>
            <div class="stat-content">
              <div class="stat-value">{{ stats.status4 || 0 }}</div>
              <div class="stat-label">已通过</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card rejected">
            <div class="stat-icon"><i class="el-icon-close"></i></div>
            <div class="stat-content">
              <div class="stat-value">{{ stats.status5 || 0 }}</div>
              <div class="stat-label">已驳回</div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 搜索筛选栏 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="searchParams" class="filter-form">
        <el-form-item label="申请编号">
          <el-input v-model="searchParams.applyNo" placeholder="请输入申请编号" clearable style="width: 180px" />
        </el-form-item>
        <el-form-item label="商户名称">
          <el-input v-model="searchParams.merchantName" placeholder="请输入商户名称" clearable style="width: 180px" />
        </el-form-item>
        <el-form-item label="审核状态">
          <el-select v-model="searchParams.applyStatus" placeholder="全部" clearable style="width: 140px">
            <el-option label="待代理初审" :value="2"></el-option>
            <el-option label="微信审核中" :value="3"></el-option>
            <el-option label="已通过" :value="4"></el-option>
            <el-option label="已驳回" :value="5"></el-option>
            <el-option label="草稿" :value="1"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
          <el-button @click="handleReset" icon="el-icon-refresh-right">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 申请列表 -->
    <el-card class="list-card">
      <div v-loading="loading">
        <el-table :data="applyList" border stripe style="width: 100%">
          <el-table-column label="申请编号" prop="applyNo" min-width="180" show-overflow-tooltip />
          <el-table-column label="商户名称" prop="merchantName" min-width="150" />
          <el-table-column label="商户简称" prop="merchantShortName" min-width="120" />
          <el-table-column label="联系人" prop="adminName" min-width="100" />
          <el-table-column label="联系电话" prop="adminPhone" min-width="120" />
          <el-table-column label="申请时间" prop="addDataTime" min-width="160">
            <template #default="scope">
              {{ formatDate(scope.row.addDataTime) }}
            </template>
          </el-table-column>
          <el-table-column label="审核阶段" min-width="100" align="center">
            <template #default="scope">
              <el-tag :type="getAuditStageType(scope.row.applyStatus)" size="small">
                {{ getStatusText(scope.row.applyStatus) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="220" fixed="right" align="center">
            <template #default="scope">
              <el-button type="primary" size="mini" @click="viewDetail(scope.row)" icon="el-icon-view">详情</el-button>
              <el-button
                  v-if="scope.row.applyStatus === 2 && !isSuperAdmin"
                  type="success"
                  size="mini"
                  @click="openAgentReviewDialog(scope.row)"
                  icon="el-icon-check"
              >代理初审</el-button>
              <el-button
                  v-if="scope.row.applyStatus === 3 && isSuperAdmin"
                  type="warning"
                  size="mini"
                  @click="openWechatReviewDialog(scope.row)"
                  icon="el-icon-connection"
              >微信审核</el-button>
            </template>
          </el-table-column>
        </el-table>

        <el-empty v-if="!loading && applyList.length === 0" description="暂无进件申请" :image-size="120" />

        <div class="pagination-container" v-if="applyList.length > 0">
          <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="pagination.page"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pagination.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="pagination.total"
              background
          />
        </div>
      </div>
    </el-card>

    <!-- 详情弹窗 -->
    <el-dialog
        title="进件申请详情"
        :visible.sync="detailDialog.visible"
        width="900px"
        @close="handleDetailDialogClose"
        append-to-body
    >
      <div class="apply-detail" v-loading="detailDialog.loading" v-if="detailDialog.data">
        <!-- 审核流程步骤条 -->
        <el-steps :active="getStepActive(detailDialog.data.applyStatus)" finish-status="success" align-center>
          <el-step title="商户提交" description="资料已提交" />
          <el-step title="代理初审" description="代理商审核" />
          <el-step title="微信审核" description="微信支付审核" />
          <el-step title="完成" description="进件完成" />
        </el-steps>

        <el-tabs v-model="activeTab" style="margin-top: 20px">
          <el-tab-pane label="基本信息" name="basic">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="申请编号">{{ detailDialog.data.applyNo || '-' }}</el-descriptions-item>
              <el-descriptions-item label="业务编号">{{ detailDialog.data.businessCode || '-' }}</el-descriptions-item>
              <el-descriptions-item label="商户名称">{{ detailDialog.data.merchantName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="商户简称">{{ detailDialog.data.merchantShortName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="主体类型">{{ getSubjectTypeText(detailDialog.data.subjectType) || '-' }}</el-descriptions-item>
              <el-descriptions-item label="客服电话">{{ detailDialog.data.servicePhone || '-' }}</el-descriptions-item>
              <el-descriptions-item label="经营场景">{{ detailDialog.data.salesSceneType || '-' }}</el-descriptions-item>
              <el-descriptions-item label="行业类目">{{ detailDialog.data.industryCategory || '-' }}</el-descriptions-item>
              <el-descriptions-item label="经营描述" :span="2">{{ detailDialog.data.businessDesc || '-' }}</el-descriptions-item>
              <el-descriptions-item label="申请时间">{{ formatDate(detailDialog.data.addDataTime) }}</el-descriptions-item>
              <el-descriptions-item label="提交时间" v-if="detailDialog.data.submitTime">{{ formatDate(detailDialog.data.submitTime) }}</el-descriptions-item>
              <el-descriptions-item label="完成时间" v-if="detailDialog.data.completeTime">{{ formatDate(detailDialog.data.completeTime) }}</el-descriptions-item>
            </el-descriptions>
          </el-tab-pane>

          <el-tab-pane label="营业执照" name="license">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="营业执照号">{{ detailDialog.data.licenseNumber || '-' }}</el-descriptions-item>
              <el-descriptions-item label="注册地址" :span="2">{{ detailDialog.data.licenseAddress || '-' }}</el-descriptions-item>
              <el-descriptions-item label="有效期开始">{{ formatDateOnly(detailDialog.data.licensePeriodBegin) || '-' }}</el-descriptions-item>
              <el-descriptions-item label="有效期结束">{{ formatDateOnly(detailDialog.data.licensePeriodEnd) || '长期有效' }}</el-descriptions-item>
              <el-descriptions-item label="营业执照" :span="2">
                <div v-if="detailDialog.data.licenseCopyUrl" class="image-container">
                  <el-image :src="detailDialog.data.licenseCopyUrl" class="detail-image" :preview-src-list="[detailDialog.data.licenseCopyUrl]" fit="contain" />
                </div>
                <span v-else>无</span>
              </el-descriptions-item>
            </el-descriptions>
          </el-tab-pane>

          <el-tab-pane label="法人信息" name="legal">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="法人姓名">{{ detailDialog.data.legalPersonName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="法人身份证号">{{ detailDialog.data.legalPersonIdCard || '-' }}</el-descriptions-item>
              <el-descriptions-item label="身份证有效期开始">{{ formatDateOnly(detailDialog.data.idCardPeriodBegin) || '-' }}</el-descriptions-item>
              <el-descriptions-item label="身份证有效期结束">{{ formatDateOnly(detailDialog.data.idCardPeriodEnd) || '长期有效' }}</el-descriptions-item>
              <el-descriptions-item label="身份证正面" :span="2">
                <div v-if="detailDialog.data.idCardFrontUrl" class="image-container">
                  <el-image :src="detailDialog.data.idCardFrontUrl" class="detail-image" :preview-src-list="[detailDialog.data.idCardFrontUrl]" fit="contain" />
                </div>
                <span v-else>无</span>
              </el-descriptions-item>
              <el-descriptions-item label="身份证反面" :span="2">
                <div v-if="detailDialog.data.idCardBackUrl" class="image-container">
                  <el-image :src="detailDialog.data.idCardBackUrl" class="detail-image" :preview-src-list="[detailDialog.data.idCardBackUrl]" fit="contain" />
                </div>
                <span v-else>无</span>
              </el-descriptions-item>
            </el-descriptions>
          </el-tab-pane>

          <!-- ===== ★★★ 联系人信息（含经办人证件）★★★ ===== -->
          <el-tab-pane label="联系人信息" name="contact">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="联系人类型">{{ detailDialog.data.adminType === 'LEGAL' ? '法人' : detailDialog.data.adminType === 'SUPERIOR' ? '经办人' : detailDialog.data.adminType || '-' }}</el-descriptions-item>
              <el-descriptions-item label="联系人姓名">{{ detailDialog.data.adminName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="联系人手机">{{ detailDialog.data.adminPhone || '-' }}</el-descriptions-item>
              <el-descriptions-item label="联系人邮箱">{{ detailDialog.data.adminEmail || '-' }}</el-descriptions-item>
              <el-descriptions-item label="联系手机">{{ detailDialog.data.contactPhone || '-' }}</el-descriptions-item>
              <el-descriptions-item label="联系邮箱">{{ detailDialog.data.contactEmail || '-' }}</el-descriptions-item>

              <!-- ★★★ 经办人证件信息 ★★★ -->
              <template v-if="detailDialog.data.adminType === 'SUPERIOR'">
                <el-descriptions-item label="证件类型">{{ detailDialog.data.contactIdDocType || '-' }}</el-descriptions-item>
                <!-- ★★★ 新增：经办人身份证号 ★★★ -->
                <el-descriptions-item label="经办人身份证号">{{ detailDialog.data.contactIdNumber || '-' }}</el-descriptions-item>
                <el-descriptions-item label="证件有效期开始">{{ formatDateOnly(detailDialog.data.contactPeriodBegin) || '-' }}</el-descriptions-item>
                <el-descriptions-item label="证件有效期结束">{{ formatDateOnly(detailDialog.data.contactPeriodEnd) || '长期有效' }}</el-descriptions-item>
                <el-descriptions-item label=" "></el-descriptions-item>
                <el-descriptions-item label="经办人证件正面" :span="2">
                  <div v-if="detailDialog.data.contactIdDocCopyUrl || detailDialog.data.idCardFrontUrl" class="image-container">
                    <el-image
                        :src="detailDialog.data.contactIdDocCopyUrl || detailDialog.data.idCardFrontUrl"
                        class="detail-image"
                        :preview-src-list="[detailDialog.data.contactIdDocCopyUrl || detailDialog.data.idCardFrontUrl]"
                        fit="contain"
                    />
                  </div>
                  <span v-else>无</span>
                </el-descriptions-item>
                <el-descriptions-item label="经办人证件反面" :span="2">
                  <div v-if="detailDialog.data.contactIdDocCopyBackUrl || detailDialog.data.idCardBackUrl" class="image-container">
                    <el-image
                        :src="detailDialog.data.contactIdDocCopyBackUrl || detailDialog.data.idCardBackUrl"
                        class="detail-image"
                        :preview-src-list="[detailDialog.data.contactIdDocCopyBackUrl || detailDialog.data.idCardBackUrl]"
                        fit="contain"
                    />
                  </div>
                  <span v-else>无</span>
                </el-descriptions-item>
                <el-descriptions-item label="授权函" :span="2">
                  <div v-if="detailDialog.data.authorizationLetterUrl" class="image-container">
                    <el-image :src="detailDialog.data.authorizationLetterUrl" class="detail-image" :preview-src-list="[detailDialog.data.authorizationLetterUrl]" fit="contain" />
                  </div>
                  <span v-else>无</span>
                </el-descriptions-item>
              </template>
            </el-descriptions>
          </el-tab-pane>

          <el-tab-pane label="经营信息" name="business">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="经营场景">{{ detailDialog.data.salesSceneType || '-' }}</el-descriptions-item>
              <el-descriptions-item label="客服电话">{{ detailDialog.data.servicePhone || '-' }}</el-descriptions-item>
              <el-descriptions-item label="行业类目">{{ detailDialog.data.industryCategory || '-' }}</el-descriptions-item>
              <el-descriptions-item label="结算规则ID">{{ detailDialog.data.settlementId || '-' }}</el-descriptions-item>
              <el-descriptions-item label="标准费率">{{ detailDialog.data.standardRate || '-' }}</el-descriptions-item>
              <el-descriptions-item label="是否有优惠">{{ detailDialog.data.hasPromotion === 1 ? '是' : '否' }}</el-descriptions-item>
              <el-descriptions-item label="优惠费率ID" v-if="detailDialog.data.hasPromotion === 1">{{ detailDialog.data.promotionId || '-' }}</el-descriptions-item>
              <el-descriptions-item label="借记卡费率">{{ detailDialog.data.debitRate || '-' }}</el-descriptions-item>
              <el-descriptions-item label="贷记卡费率">{{ detailDialog.data.creditRate || '-' }}</el-descriptions-item>
              <el-descriptions-item label="经营描述" :span="2">{{ detailDialog.data.businessDesc || '-' }}</el-descriptions-item>
            </el-descriptions>
          </el-tab-pane>

          <el-tab-pane label="门店照片" name="store" v-if="detailDialog.data.salesSceneType && detailDialog.data.salesSceneType.includes('STORE')">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="门店入口图" :span="2">
                <div v-if="detailDialog.data.storeEntranceUrl" class="image-container">
                  <el-image :src="detailDialog.data.storeEntranceUrl" class="detail-image" :preview-src-list="[detailDialog.data.storeEntranceUrl]" fit="contain" />
                </div>
                <span v-else>无</span>
              </el-descriptions-item>
              <el-descriptions-item label="店内环境图" :span="2">
                <div v-if="detailDialog.data.storeInteriorUrl" class="image-container">
                  <el-image :src="detailDialog.data.storeInteriorUrl" class="detail-image" :preview-src-list="[detailDialog.data.storeInteriorUrl]" fit="contain" />
                </div>
                <span v-else>无</span>
              </el-descriptions-item>
            </el-descriptions>
          </el-tab-pane>

          <el-tab-pane label="银行账户" name="bank">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="账户类型">{{ detailDialog.data.bankAccountType || '-' }}</el-descriptions-item>
              <el-descriptions-item label="开户名称">{{ detailDialog.data.accountName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="银行账号">{{ maskBankAccount(detailDialog.data.accountNumber) || '-' }}</el-descriptions-item>
              <el-descriptions-item label="开户银行">{{ detailDialog.data.accountBank || '-' }}</el-descriptions-item>
              <el-descriptions-item label="开户支行">{{ detailDialog.data.bankName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="联行号">{{ detailDialog.data.bankBranchId || '-' }}</el-descriptions-item>
              <el-descriptions-item label="银行地址码">{{ detailDialog.data.bankAddressCode || '-' }}</el-descriptions-item>
            </el-descriptions>
          </el-tab-pane>

          <el-tab-pane label="补充材料" name="addition">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="资质图片" :span="2">
                <div v-if="detailDialog.data.qualificationUrl" class="image-container">
                  <el-image :src="detailDialog.data.qualificationUrl" class="detail-image" :preview-src-list="[detailDialog.data.qualificationUrl]" fit="contain" />
                </div>
                <span v-else>无</span>
              </el-descriptions-item>
              <el-descriptions-item label="补充描述" :span="2">{{ detailDialog.data.additionDesc || '-' }}</el-descriptions-item>
              <el-descriptions-item label="法人承诺函" :span="2" v-if="detailDialog.data.legalPersonCommitmentUrl">
                <div class="image-container">
                  <el-image :src="detailDialog.data.legalPersonCommitmentUrl" class="detail-image" :preview-src-list="[detailDialog.data.legalPersonCommitmentUrl]" fit="contain" />
                </div>
              </el-descriptions-item>
              <el-descriptions-item label="补充材料说明" :span="2">{{ detailDialog.data.businessAdditionMsg || '-' }}</el-descriptions-item>
            </el-descriptions>
          </el-tab-pane>

          <el-tab-pane label="审核信息" name="audit">
            <el-descriptions :column="1" border>
              <el-descriptions-item label="当前阶段">
                <el-tag :type="getStatusType(detailDialog.data.applyStatus)" size="small">{{ getStatusText(detailDialog.data.applyStatus) }}</el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="微信状态" v-if="detailDialog.data.wechatStatus">
                {{ getWechatStatusText(detailDialog.data.wechatStatus) }}
              </el-descriptions-item>
              <el-descriptions-item label="微信状态描述" v-if="detailDialog.data.wechatStatusDesc">{{ detailDialog.data.wechatStatusDesc || '-' }}</el-descriptions-item>
              <el-descriptions-item label="审核人ID" v-if="detailDialog.data.auditAdminId">{{ detailDialog.data.auditAdminId }}</el-descriptions-item>
              <el-descriptions-item label="审核时间" v-if="detailDialog.data.auditTime">{{ formatDate(detailDialog.data.auditTime) }}</el-descriptions-item>
              <el-descriptions-item label="驳回原因" v-if="detailDialog.data.rejectReason"><span style="color: #f56c6c;">{{ detailDialog.data.rejectReason }}</span></el-descriptions-item>
              <el-descriptions-item label="驳回详情" v-if="detailDialog.data.rejectDetails"><span style="color: #f56c6c;">{{ detailDialog.data.rejectDetails }}</span></el-descriptions-item>
              <el-descriptions-item label="微信申请单号" v-if="detailDialog.data.wechatApplyId">{{ detailDialog.data.wechatApplyId }}</el-descriptions-item>
              <el-descriptions-item label="微信子商户号" v-if="detailDialog.data.wechatSubMerchantId">{{ detailDialog.data.wechatSubMerchantId }}</el-descriptions-item>
              <el-descriptions-item label="签约链接" v-if="detailDialog.data.signUrl">{{ detailDialog.data.signUrl }}</el-descriptions-item>
              <el-descriptions-item label="签约状态" v-if="detailDialog.data.signState">{{ detailDialog.data.signState }}</el-descriptions-item>
              <el-descriptions-item label="微信错误信息" v-if="detailDialog.data.wechatErrorMsg"><span style="color: #f56c6c;">{{ detailDialog.data.wechatErrorMsg }}</span></el-descriptions-item>
            </el-descriptions>
          </el-tab-pane>
        </el-tabs>
      </div>
      <span slot="footer">
        <el-button @click="detailDialog.visible = false">关闭</el-button>
        <el-button v-if="detailDialog.data?.applyStatus === 2 && !isSuperAdmin" type="success" @click="openAgentReviewDialogFromDetail">代理初审</el-button>
        <el-button v-if="detailDialog.data?.applyStatus === 3 && isSuperAdmin" type="warning" @click="openWechatReviewDialogFromDetail">微信审核</el-button>
      </span>
    </el-dialog>

    <!-- 代理初审弹窗 -->
    <el-dialog title="代理初审" :visible.sync="agentReviewDialog.visible" width="500px" append-to-body @close="handleAgentReviewDialogClose">
      <el-form :model="agentReviewDialog.form" label-width="100px">
        <el-form-item label="商户名称"><span>{{ agentReviewDialog.form.merchantName }}</span></el-form-item>
        <el-form-item label="初审结果" prop="result" required>
          <el-radio-group v-model="agentReviewDialog.form.result">
            <el-radio :label="1">通过（提交微信审核）</el-radio>
            <el-radio :label="0">拒绝（退回商户）</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="驳回原因" v-if="agentReviewDialog.form.result === 0" required>
          <el-input v-model="agentReviewDialog.form.rejectReason" type="textarea" :rows="4" placeholder="请输入驳回原因..." maxlength="500" show-word-limit />
        </el-form-item>
        <el-form-item label="初审意见" v-else>
          <el-input v-model="agentReviewDialog.form.remark" type="textarea" :rows="3" placeholder="请输入初审意见（选填）" maxlength="200" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="agentReviewDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="confirmAgentReview" :loading="agentReviewDialog.loading">提交初审</el-button>
      </span>
    </el-dialog>

    <!-- 微信审核弹窗 -->
    <el-dialog title="微信审核" :visible.sync="wechatReviewDialog.visible" width="500px" append-to-body @close="handleWechatReviewDialogClose">
      <el-form :model="wechatReviewDialog.form" label-width="100px">
        <el-form-item label="商户名称"><span>{{ wechatReviewDialog.form.merchantName }}</span></el-form-item>
        <el-form-item label="微信状态" prop="wechatStatus" required>
          <el-select v-model="wechatReviewDialog.form.wechatStatus" placeholder="请选择微信审核结果" style="width: 100%">
            <el-option label="审核通过" value="PASSED"></el-option>
            <el-option label="审核驳回" value="REJECTED"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="驳回原因" v-if="wechatReviewDialog.form.wechatStatus === 'REJECTED'" required>
          <el-input v-model="wechatReviewDialog.form.rejectReason" type="textarea" :rows="4" placeholder="请输入微信驳回原因..." maxlength="500" show-word-limit />
        </el-form-item>
        <el-form-item label="子商户号" v-if="wechatReviewDialog.form.wechatStatus === 'PASSED'" required>
          <el-input v-model="wechatReviewDialog.form.subMerchantId" placeholder="请输入微信子商户号" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="wechatReviewDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="confirmWechatReview" :loading="wechatReviewDialog.loading">提交审核结果</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader";
import api from "@/utils/api";

export default {
  name: "ApplyReview",
  components: { PageHeader },
  data() {
    return {
      loading: false,
      isSuperAdmin: false,
      activeTab: 'basic',

      searchParams: { applyNo: '', merchantName: '', applyStatus: null },
      stats: { status1: 0, status2: 0, status3: 0, status4: 0, status5: 0 },
      applyList: [],
      pagination: { page: 1, pageSize: 10, total: 0 },

      detailDialog: { visible: false, loading: false, data: null },

      agentReviewDialog: {
        visible: false, loading: false,
        form: { id: null, merchantName: '', result: 1, rejectReason: '', remark: '' }
      },

      wechatReviewDialog: {
        visible: false, loading: false,
        form: { id: null, merchantName: '', wechatStatus: 'PASSED', rejectReason: '', subMerchantId: '' }
      }
    };
  },
  created() {
    this.getUserRole();
    this.loadStats();
    this.loadList();
  },
  methods: {
    getUserRole() {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const roles = userInfo.roles || [];
      this.isSuperAdmin = roles.includes('SUPER_ADMIN') || userInfo.role === 'SUPER_ADMIN';
    },
    getWechatStatusText(status) {
      const map = {
        'APPLYMENT_STATUS_CHECKING': '审核中',
        'APPLYMENT_STATUS_PASSED': '已通过',
        'APPLYMENT_STATUS_REJECTED': '已驳回',
        'APPLYMENT_STATUS_CANCELED': '已撤销',
        'APPLYMENT_STATUS_FREEZED': '已冻结'
      };
      return map[status] || status || '-';
    },

    // ==================== ★★★ 图片加载（新增经办人图片）★★★ ====================
    async loadDetailImages(data) {
      if (!data) return;
      console.log('========== [ApplyReview] loadDetailImages 开始 ==========')
      console.log('[ApplyReview] 加载图片，数据字段:')
      console.log('  contactIdDocCopyImageId:', data.contactIdDocCopyImageId)
      console.log('  contactIdDocCopyBackImageId:', data.contactIdDocCopyBackImageId)
      console.log('  authorizationLetterImageId:', data.authorizationLetterImageId)

      const imageFields = [
        { id: data.licenseCopyImageId, setter: (url) => { this.$set(data, 'licenseCopyUrl', url) } },
        { id: data.idCardFrontImageId, setter: (url) => { this.$set(data, 'idCardFrontUrl', url) } },
        { id: data.idCardBackImageId, setter: (url) => { this.$set(data, 'idCardBackUrl', url) } },
        // ★★★ 经办人证件图片 ★★★
        { id: data.contactIdDocCopyImageId, setter: (url) => { this.$set(data, 'contactIdDocCopyUrl', url) } },
        { id: data.contactIdDocCopyBackImageId, setter: (url) => { this.$set(data, 'contactIdDocCopyBackUrl', url) } },
        { id: data.storeEntranceImageId, setter: (url) => { this.$set(data, 'storeEntranceUrl', url) } },
        { id: data.storeInteriorImageId, setter: (url) => { this.$set(data, 'storeInteriorUrl', url) } },
        { id: data.authorizationLetterImageId, setter: (url) => { this.$set(data, 'authorizationLetterUrl', url) } },
        { id: data.qualificationImageId, setter: (url) => { this.$set(data, 'qualificationUrl', url) } },
        { id: data.legalPersonCommitmentImageId, setter: (url) => { this.$set(data, 'legalPersonCommitmentUrl', url) } }
      ];
      const promises = [];
      for (const field of imageFields) {
        if (field.id) {
          console.log('[ApplyReview] 加载图片 ID:', field.id)
          promises.push(
              api.image_getImageById({ id: field.id }).then(res => {
                const responseData = res.data || res;
                if (responseData.code === '000' && responseData.data) {
                  field.setter(responseData.data.url || responseData.data.path);
                  console.log('[ApplyReview] ✅ 图片加载成功:', responseData.data.url)
                }
              }).catch(e => console.error(`加载图片失败 (ID: ${field.id}):`, e))
          );
        }
      }
      await Promise.all(promises);
      console.log('========== [ApplyReview] loadDetailImages 结束 ==========')
    },

    // ==================== 数据加载 ====================
    async loadStats() {
      try {
        const res = await api.subMerchant_getApplyStats();
        const data = res.data || res;
        if (data && (data.code === "000" || data.success)) {
          this.stats = data.data || {};
        }
      } catch (e) { console.error('加载统计失败:', e) }
    },

    async loadList() {
      this.loading = true;
      try {
        const res = await api.subMerchant_getApplyList({
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
          applyNo: this.searchParams.applyNo || undefined,
          merchantName: this.searchParams.merchantName || undefined,
          applyStatus: this.searchParams.applyStatus || undefined
        });
        const data = res.data || res;
        if (data && (data.code === "000" || data.success)) {
          this.applyList = data.data?.list || [];
          this.pagination.total = data.data?.total || 0;
        } else {
          this.$message.error(data?.message || '加载失败');
        }
      } catch (e) { console.error('加载列表失败:', e); this.$message.error('加载失败') }
      finally { this.loading = false }
    },

    handleSearch() { this.pagination.page = 1; this.loadStats(); this.loadList() },
    handleReset() { this.searchParams = { applyNo: '', merchantName: '', applyStatus: null }; this.pagination.page = 1; this.loadList() },
    handleSizeChange(s) { this.pagination.pageSize = s; this.pagination.page = 1; this.loadList() },
    handleCurrentChange(p) { this.pagination.page = p; this.loadList() },

    // ==================== 详情 ====================
    async viewDetail(row) {
      console.log('========== [ApplyReview] viewDetail 开始 ==========')
      console.log('[ApplyReview] 查看详情数据:', row)
      this.detailDialog.data = { ...row };
      this.detailDialog.visible = true;
      this.detailDialog.loading = true;
      this.activeTab = 'basic';
      await this.loadDetailImages(this.detailDialog.data);
      console.log('[ApplyReview] 加载完成后的数据:', this.detailDialog.data)
      console.log('  contactIdDocCopyUrl:', this.detailDialog.data.contactIdDocCopyUrl)
      console.log('  contactIdDocCopyBackUrl:', this.detailDialog.data.contactIdDocCopyBackUrl)
      console.log('  authorizationLetterUrl:', this.detailDialog.data.authorizationLetterUrl)
      this.detailDialog.loading = false;
      console.log('========== [ApplyReview] viewDetail 结束 ==========')
    },

    // ==================== 代理初审 ====================
    openAgentReviewDialog(row) {
      console.log('========== [ApplyReview] openAgentReviewDialog 开始 ==========')
      console.log('[ApplyReview] 代理初审数据:', row)
      this.agentReviewDialog.form = {
        id: row.id, merchantName: row.merchantName || row.merchantShortName,
        result: 1, rejectReason: '', remark: ''
      };
      this.agentReviewDialog.visible = true;
    },
    openAgentReviewDialogFromDetail() {
      const d = this.detailDialog.data;
      this.detailDialog.visible = false;
      this.$nextTick(() => this.openAgentReviewDialog(d));
    },
    async confirmAgentReview() {
      if (this.agentReviewDialog.form.result === 0 && !this.agentReviewDialog.form.rejectReason) {
        return this.$message.warning('请填写驳回原因');
      }
      this.agentReviewDialog.loading = true;
      try {
        const params = {
          id: this.agentReviewDialog.form.id,
          applyStatus: this.agentReviewDialog.form.result === 1 ? 3 : 5,
          rejectReason: this.agentReviewDialog.form.result === 0 ? this.agentReviewDialog.form.rejectReason : '',
          remark: this.agentReviewDialog.form.remark
        };
        console.log('========== [ApplyReview] 代理初审提交参数 ==========')
        console.log(JSON.stringify(params, null, 2))

        const res = await api.subMerchant_agentReview(params);
        const data = res.data || res;
        console.log('[ApplyReview] 代理初审响应:', data)
        if (data && (data.code === "000" || data.success)) {
          this.$message.success(data.message || (this.agentReviewDialog.form.result === 1 ? '初审通过，已提交微信审核' : '已驳回'));
          this.agentReviewDialog.visible = false;
          this.loadStats(); this.loadList();
        } else { this.$message.error(data?.message || '操作失败') }
      } catch (e) { console.error('初审失败:', e); this.$message.error('操作失败') }
      finally { this.agentReviewDialog.loading = false }
    },

    // ==================== 微信审核 ====================
    openWechatReviewDialog(row) {
      console.log('========== [ApplyReview] openWechatReviewDialog 开始 ==========')
      console.log('[ApplyReview] 微信审核数据:', row)
      this.wechatReviewDialog.form = {
        id: row.id, merchantName: row.merchantName || row.merchantShortName,
        wechatStatus: 'PASSED', rejectReason: '', subMerchantId: ''
      };
      this.wechatReviewDialog.visible = true;
    },
    openWechatReviewDialogFromDetail() {
      const d = this.detailDialog.data;
      this.detailDialog.visible = false;
      this.$nextTick(() => this.openWechatReviewDialog(d));
    },
    async confirmWechatReview() {
      if (this.wechatReviewDialog.form.wechatStatus === 'REJECTED' && !this.wechatReviewDialog.form.rejectReason) {
        return this.$message.warning('请填写驳回原因');
      }
      if (this.wechatReviewDialog.form.wechatStatus === 'PASSED' && !this.wechatReviewDialog.form.subMerchantId) {
        return this.$message.warning('请填写微信子商户号');
      }
      this.wechatReviewDialog.loading = true;
      try {
        const params = {
          id: this.wechatReviewDialog.form.id,
          wechatStatus: this.wechatReviewDialog.form.wechatStatus,
          rejectReason: this.wechatReviewDialog.form.rejectReason,
          subMerchantId: this.wechatReviewDialog.form.subMerchantId
        };
        console.log('========== [ApplyReview] 微信审核提交参数 ==========')
        console.log(JSON.stringify(params, null, 2))

        const res = await api.subMerchant_wechatReview(params);
        const data = res.data || res;
        console.log('[ApplyReview] 微信审核响应:', data)
        if (data && (data.code === "000" || data.success)) {
          this.$message.success(data.message || (this.wechatReviewDialog.form.wechatStatus === 'PASSED' ? '微信审核通过，进件完成' : '微信审核驳回'));
          this.wechatReviewDialog.visible = false;
          this.loadStats(); this.loadList();
        } else { this.$message.error(data?.message || '操作失败') }
      } catch (e) { console.error('微信审核失败:', e); this.$message.error('操作失败') }
      finally { this.wechatReviewDialog.loading = false }
    },

    // ==================== 工具方法 ====================
    getAuditStageType(s) { return { 2: 'warning', 3: 'primary', 4: 'success', 5: 'danger' }[s] || 'info' },
    getStatusType(s) { return { 1: 'info', 2: 'warning', 3: 'primary', 4: 'success', 5: 'danger' }[s] || 'info' },
    getStatusText(s) { return { 1: '草稿', 2: '待代理初审', 3: '微信审核中', 4: '已通过', 5: '已驳回' }[s] || '未知' },
    getSubjectTypeText(t) {
      return { 'SUBJECT_TYPE_INDIVIDUAL': '个体工商户', 'SUBJECT_TYPE_ENTERPRISE': '企业', 'SUBJECT_TYPE_GOVERNMENT': '政府机关', 'SUBJECT_TYPE_INSTITUTIONS': '事业单位', 'SUBJECT_TYPE_OTHERS': '其他组织' }[t] || t;
    },
    getStepActive(s) { if (s === 1) return 0; if (s === 2) return 1; if (s === 3) return 2; if (s === 4) return 3; if (s === 5) return 1; return 0 },
    formatDate(ts) {
      if (!ts) return '-'; const d = new Date(ts); if (isNaN(d.getTime())) return '-';
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`;
    },
    formatDateOnly(v) {
      if (!v) return '-'; const d = new Date(v); if (isNaN(d.getTime())) return String(v);
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    },
    maskBankAccount(a) { if (!a) return '-'; if (a.length <= 8) return '****'; return a.substring(0,4)+'****'+a.substring(a.length-4) },

    handleDetailDialogClose() { this.detailDialog.data = null; this.detailDialog.loading = false; this.activeTab = 'basic' },
    handleAgentReviewDialogClose() { this.agentReviewDialog.form = { id: null, merchantName: '', result: 1, rejectReason: '', remark: '' } },
    handleWechatReviewDialogClose() { this.wechatReviewDialog.form = { id: null, merchantName: '', wechatStatus: 'PASSED', rejectReason: '', subMerchantId: '' } }
  }
};
</script>

<style lang="scss" scoped>
.apply-review-page { padding: 20px; background-color: #f5f7fa; min-height: 100vh; }
.stats-cards { margin-bottom: 20px; }
.stat-card {
  background: white; border-radius: 8px; padding: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  display: flex; align-items: center; height: 100px;
  .stat-icon { width: 60px; height: 60px; border-radius: 8px; display: flex; align-items: center; justify-content: center; margin-right: 16px; font-size: 28px; }
  .stat-content { flex: 1; .stat-value { font-size: 28px; font-weight: 600; margin-bottom: 4px; } .stat-label { font-size: 14px; color: #666; } }
  &.pending .stat-icon { background-color: #fff7e6; color: #fa8c16; }
  &.pending .stat-value { color: #fa8c16; }
  &.reviewing .stat-icon { background-color: #e6f7ff; color: #1890ff; }
  &.reviewing .stat-value { color: #1890ff; }
  &.approved .stat-icon { background-color: #f6ffed; color: #52c41a; }
  &.approved .stat-value { color: #52c41a; }
  &.rejected .stat-icon { background-color: #fff2f0; color: #f56c6c; }
  &.rejected .stat-value { color: #f56c6c; }
}
.filter-card, .list-card { border-radius: 8px; margin-bottom: 20px; }
.filter-form { margin-bottom: 0; }
.pagination-container { margin-top: 20px; display: flex; justify-content: flex-end; padding-top: 16px; border-top: 1px solid #ebeef5; }
.apply-detail { .el-steps { margin-bottom: 20px; } }
.image-container { text-align: center;
  .detail-image { max-width: 300px; max-height: 200px; border-radius: 4px; cursor: pointer; border: 1px solid #ebeef5; &:hover { opacity: 0.8; } }
}
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 3px; }
::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
@media (max-width: 1200px) { .filter-card :deep(.el-form-item) { margin-bottom: 12px; margin-right: 12px; } }
@media (max-width: 768px) {
  .filter-card :deep(.el-form-item) { width: 100%; margin-right: 0;
    .el-form-item__content { width: 100%; .el-input, .el-select { width: 100%; } }
  }
}
</style>